import React, { useRef, useState, useEffect } from "react";
import { Play, Pause, Clock, Tag, Star, Maximize } from "lucide-react";
import { Reel } from "../sample.data";
import { getVisibleSortedItems, getFeaturedItems } from "../utils";

interface ReelsProps {
  reels: Reel[];
  showFeaturedOnly?: boolean;
}

export const ReelsSection: React.FC<ReelsProps> = ({
  reels,
  showFeaturedOnly = false,
}) => {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [hoveredReel, setHoveredReel] = useState<string | null>(null);
  const [progress, setProgress] = useState<Record<string, number>>({});
  const refs = useRef<Record<string, HTMLVideoElement | null>>({});

  const visibleReels = showFeaturedOnly
    ? getFeaturedItems(reels)
    : getVisibleSortedItems(reels);

  const toggle = (id: string) => {
    const vid = refs.current[id];
    if (!vid) return;

    if (playingId === id) {
      vid.pause();
      setPlayingId(null);
    } else {
      // Pause all other videos
      Object.values(refs.current).forEach((v) => v?.pause());
      vid.play();
      setPlayingId(id);
    }
  };

  const toggleFullscreen = (id: string) => {
    const vid = refs.current[id];
    if (!vid) return;

    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      vid.requestFullscreen().catch((err) => {
        console.log("Fullscreen error:", err);
      });
    }
  };

  const handleTimeUpdate = (id: string) => {
    const vid = refs.current[id];
    if (!vid) return;

    const progressPercent = (vid.currentTime / vid.duration) * 100;
    setProgress((prev) => ({
      ...prev,
      [id]: progressPercent,
    }));
  };

  // Initialize video event listeners
  useEffect(() => {
    Object.entries(refs.current).forEach(([id, vid]) => {
      if (vid) {
        vid.addEventListener("timeupdate", () => handleTimeUpdate(id));
        vid.addEventListener("ended", () => {
          setPlayingId(null);
          setProgress((prev) => ({ ...prev, [id]: 0 }));
        });
      }
    });

    return () => {
      Object.values(refs.current).forEach((vid) => {
        if (vid) {
          vid.removeEventListener("timeupdate", () => handleTimeUpdate);
          vid.removeEventListener("ended", () => setPlayingId(null));
        }
      });
    };
  }, [visibleReels]);

  const getCategoryColor = (category?: string) => {
    if (!category) return "from-gray-500 to-gray-600";

    const colors = {
      demo: "from-blue-500 to-indigo-600",
      tutorial: "from-emerald-500 to-green-600",
      showcase: "from-purple-500 to-pink-600",
      "behind-the-scenes": "from-amber-500 to-orange-600",
      testimonial: "from-rose-500 to-red-600",
    };

    return (
      colors[category.toLowerCase() as keyof typeof colors] ||
      "from-gray-500 to-gray-600"
    );
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  if (visibleReels.length === 0) return null;

  return (
    <div className="relative">
      {/* Horizontal Scroll Container */}
      <div className="relative overflow-x-auto pb-6">
        <div className="flex gap-4 sm:gap-6 pb-2">
          {visibleReels.map((reel) => {
            const isHovered = hoveredReel === reel.id;
            const isPlaying = playingId === reel.id;

            return (
              <article
                key={reel.id}
                className="group relative h-[400px] w-[240px] sm:h-[500px] sm:w-[280px] flex-shrink-0 overflow-hidden rounded-3xl bg-white/90 backdrop-blur-xl border border-white/20 hover:bg-white/95 transition-all duration-700 hover:shadow-2xl hover:shadow-indigo-500/20"
                onMouseEnter={() => setHoveredReel(reel.id)}
                onMouseLeave={() => setHoveredReel(null)}
              >
                {/* Video Container */}
                <div
                  className="relative h-full w-full overflow-hidden rounded-3xl cursor-pointer"
                  onClick={() => toggle(reel.id)}
                >
                  <video
                    ref={(el) => (refs.current[reel.id] = el)}
                    src={reel.videoUrl}
                    poster={reel.poster}
                    className={`h-full w-full object-cover transition-all duration-700 ${
                      isHovered ? "scale-105" : "scale-100"
                    }`}
                    playsInline
                    muted={false}
                    loop
                    controls={false}
                  />

                  {/* Gradient Overlays */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
                  <div
                    className={`absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-purple-600/10 transition-opacity duration-700 ${
                      isHovered ? "opacity-100" : "opacity-0"
                    }`}
                  />

                  {/* Featured Badge */}
                  {reel.featured && (
                    <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-20 flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-full text-xs font-medium shadow-lg backdrop-blur-sm">
                      <Star className="h-2.5 w-2.5 sm:h-3 sm:w-3 fill-current" />
                      <span className="hidden sm:inline">Featured</span>
                    </div>
                  )}

                  {/* Duration Badge */}
                  {reel.duration && (
                    <div className="absolute top-3 left-3 sm:top-4 sm:left-4 z-20 flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 bg-black/60 backdrop-blur-sm text-white rounded-full text-xs font-medium">
                      <Clock className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                      {formatDuration(reel.duration)}
                    </div>
                  )}

                  {/* Category Tag */}
                  {reel.category && (
                    <div
                      className={`absolute ${
                        reel.duration ? "top-12 sm:top-16" : "top-3 sm:top-4"
                      } left-3 sm:left-4 z-20 flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 bg-gradient-to-r ${getCategoryColor(
                        reel.category
                      )} text-white rounded-full text-xs font-medium shadow-lg`}
                    >
                      <Tag className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                      {reel.category}
                    </div>
                  )}

                  {/* Content Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 z-20 p-4 sm:p-6">
                    {/* Title and Description */}
                    <div className="mb-3 sm:mb-4">
                      <h3 className="font-bold text-white text-lg sm:text-xl leading-tight mb-1 sm:mb-2 drop-shadow-lg">
                        {reel.title}
                      </h3>
                      {reel.description && (
                        <p className="text-white/90 text-xs sm:text-sm leading-relaxed drop-shadow line-clamp-2 sm:line-clamp-3">
                          {reel.description}
                        </p>
                      )}
                    </div>

                    {/* Control Panel */}
                    <div className="flex items-center justify-between">
                      {/* Play/Pause Button */}
                      <button
                        onClick={() => toggle(reel.id)}
                        className={`flex items-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-xl sm:rounded-2xl font-medium text-white transition-all duration-300 backdrop-blur-sm text-sm ${
                          isPlaying
                            ? "bg-red-500/80 hover:bg-red-600/80 shadow-lg shadow-red-500/25"
                            : "bg-blue-500/80 hover:bg-blue-600/80 shadow-lg shadow-blue-500/25"
                        }`}
                      >
                        {isPlaying ? (
                          <>
                            <Pause
                              className="h-4 w-4 sm:h-5 sm:w-5"
                              fill="currentColor"
                            />
                            <span className="hidden sm:inline">Pause</span>
                          </>
                        ) : (
                          <>
                            <Play
                              className="h-4 w-4 sm:h-5 sm:w-5"
                              fill="currentColor"
                            />
                            <span className="hidden sm:inline">Play</span>
                          </>
                        )}
                      </button>

                      {/* Additional Controls */}
                      <div className="flex items-center gap-2">
                        {/* Fullscreen Toggle */}
                        <button
                          onClick={() => toggleFullscreen(reel.id)}
                          className="p-2 sm:p-2.5 bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl text-white hover:bg-white/30 transition-colors duration-200"
                          title="View in fullscreen"
                        >
                          <Maximize className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Progress Indicator */}
                  {isPlaying && (
                    <div className="absolute bottom-0 left-0 right-0 z-10 h-1 bg-white/20">
                      <div
                        className="h-full bg-gradient-to-r from-blue-400 to-purple-500 transition-all duration-200"
                        style={{ width: `${progress[reel.id] || 0}%` }}
                      />
                    </div>
                  )}

                  {/* Loading State */}
                  {!isPlaying && (
                    <div
                      className={`absolute inset-0 z-10 flex items-center justify-center transition-opacity duration-300 ${
                        isHovered ? "opacity-0" : "opacity-100"
                      }`}
                    >
                      <div className="p-4 bg-black/40 backdrop-blur-sm rounded-full">
                        <Play
                          className="h-12 w-12 text-white drop-shadow-lg"
                          fill="currentColor"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Card Glow Effect */}
                <div
                  className={`absolute inset-0 rounded-3xl transition-all duration-700 pointer-events-none ${
                    isHovered
                      ? "shadow-2xl shadow-indigo-500/30 ring-1 ring-indigo-200/50"
                      : "shadow-lg shadow-black/10"
                  }`}
                />
              </article>
            );
          })}
        </div>
      </div>

      {/* Scroll Indicators */}
      <div className="flex justify-center mt-4 gap-2">
        {visibleReels.map((_, index) => (
          <div
            key={index}
            className="h-2 w-8 bg-gray-200 rounded-full overflow-hidden"
          >
            <div className="h-full bg-gradient-to-r from-blue-500 to-purple-600 rounded-full transform transition-transform duration-300" />
          </div>
        ))}
      </div>
    </div>
  );
};
